/**
 * Turns a Firebase auth error into a message we can put in front of the user. Shared by the sign-in
 * page and by the service that resolves a Google sign-in after the browser redirects back.
 *
 * Because both the password and Google flows share this, only codes that can only arise from the
 * Google flow (pop-ups, redirects, browser storage, provider configuration) name Google. Anything
 * that either flow can hit stays neutral, so a password failure never reports a Google problem.
 */

/** Shown when we know what went wrong but only in general terms. */
const GENERIC_SIGN_IN_FAILURE = 'Sorry, we could not sign you in. Please try again shortly or contact support.';

/**
 * Shown when App Check could not mint a token for this browser, so the request was rejected before
 * Auth ever saw the credentials.
 *
 * Actionable, and deliberately says nothing about the security mechanism: the user needs to know
 * what to change, not what we are checking.
 */
export const APP_CHECK_BLOCKED_MESSAGE = 'We couldn\'t verify your browser. A privacy extension or '
  + 'strict tracking protection may be blocking a security check on this site. Try allowlisting '
  + 'this domain, or signing in from a different browser.';

/**
 * Codes we have something specific to say about. Anything absent from this map falls through to a
 * generic message, and `explainAuthError` reports that as inconclusive.
 *
 * A Map rather than an object literal on purpose: `error.code` is whatever was on the caught error,
 * so a plain object would resolve `toString` or `constructor` off Object.prototype and hand back a
 * function as the user's error message.
 */
const AUTH_ERROR_MESSAGES = new Map<string, string>(Object.entries({
  'auth/invalid-email': 'Sorry, that is not a valid email address.',
  'auth/user-disabled': 'Sorry, your account has been disabled. Please contact us for more information.',
  'auth/user-not-found': 'Sorry, a user with that email address could not be found.',

  // With email enumeration protection enabled — as it is on this project — the Identity Platform
  // collapses a wrong password and an unknown email into `auth/invalid-credential`. The other two
  // still arrive from flows and projects where it is off, so all three stay mapped.
  'auth/wrong-password': 'Sorry, your sign in details are not correct. Please try again.',
  'auth/invalid-credential': 'Sorry, your sign in details are not correct. Please try again.',

  'auth/weak-password': 'Your password needs to be updated to meet TRACit\'s password requirements. Use Set or reset your password to continue.',
  'auth/password-does-not-meet-requirements': 'Your password needs to be updated to meet TRACit\'s password requirements. Use Set or reset your password to continue.',

  // Google-only: these can only happen in the pop-up or redirect flow.
  'auth/redirect-cancelled-by-user': 'Google sign-in was cancelled.',
  'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
  'auth/cancelled-popup-request': 'Google sign-in was cancelled.',
  'auth/popup-blocked': 'Your browser blocked the Google sign-in window. Allow pop-ups for this site and try again.',
  'auth/redirect-operation-pending': 'Google sign-in is already in progress. Please wait a moment and try again.',
  'auth/web-storage-unsupported': 'Your browser is blocking the storage Google sign-in needs. Allow cookies for this site and try again.',
  'auth/account-exists-with-different-credential': 'An account already exists for that email address. Sign in with your password, then contact support to turn on Google sign-in.',
  'auth/operation-not-allowed': 'That sign-in method is not available for TRACit yet. Please contact support.',
  'auth/unauthorized-domain': 'This site is not set up for Google sign-in yet. Please contact support.',

  // Either flow can hit these, so they stay neutral.
  'auth/timeout': 'That took too long to respond. Please try again.',
  'auth/network-request-failed': 'We could not connect. Check your internet connection and try again.',
}));

/**
 * Codes that are recognised but tell us nothing beyond "it failed". Treated the same as an
 * unrecognised code when deciding whether it is worth looking for another explanation.
 */
const INCONCLUSIVE_AUTH_ERROR_CODES = new Set([
  'auth/internal-error',
  'auth/error-code:-47',
]);

export interface AuthErrorExplanation {
  /** The message to show the user. */
  message: string;
  /**
   * False when all we have is a generic "something went wrong". App Check rejections arrive as an
   * unmapped, slugified server code, so they land here — which is the signal to go and ask App
   * Check directly rather than guess. True means we know what happened and the caller must not
   * second-guess it: someone who mistyped their password is never told to check their ad blocker.
   */
  isSpecific: boolean;
}

export function explainAuthError(error: any): AuthErrorExplanation {
  const errorCode = error?.code;
  const errorMessage = error?.message ?? '';

  if (errorCode === 'auth/internal-error') {
    // The blocking function that keeps TRACit staff on Google SSO reports itself through the
    // server error message, which is the one internal error we can explain.
    return errorMessage.includes('TRACit staff must sign in with Google')
      ? {
          message: 'TRACit accounts sign in with Google. Use the Sign in with Google button to continue.',
          isSpecific: true,
        }
      : { message: GENERIC_SIGN_IN_FAILURE, isSpecific: false };
  }

  if (INCONCLUSIVE_AUTH_ERROR_CODES.has(errorCode)) {
    return { message: GENERIC_SIGN_IN_FAILURE, isSpecific: false };
  }

  const message = AUTH_ERROR_MESSAGES.get(errorCode);

  if (message) {
    return { message, isSpecific: true };
  }

  return {
    message: errorCode
      ? `Sorry, we could not sign you in. Please contact support and quote this code: ${errorCode}`
      : 'An unknown error occurred.',
    isSpecific: false,
  };
}

export function authErrorMessage(error: any): string {
  return explainAuthError(error).message;
}

/**
 * Whether this error's message tells the user to get in touch. Those are the
 * ones worth pairing with a control that actually opens Support, rather than
 * telling someone to make contact and leaving them to find out how.
 *
 * Read off the copy itself so the two cannot drift: a message that stops asking
 * the user to contact anyone stops offering the button, with no second list to
 * keep in step.
 */
export function authErrorSuggestsSupport(error: any): boolean {
  return authErrorMessage(error).toLowerCase().includes('contact');
}
